import React from 'react'
import { Text } from '@retailer-platform/shared-components/src/tds'
import { colors } from '@retailer-platform/shared-components/src/foundation/colors'
import { useDashMessages } from '../../../intl/intl.hooks'
import AiButtonCtaModal from './AiButtonCtaModal'
import AiGradients from './AiGradients'
import { AnimatedSparkleIcon } from './AnimatedSparkleIcon'

export interface AiButtonProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  width?: number | string
  height?: number | string
  isPowered?: boolean
  children?: React.ReactNode
  onClick?: (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void
  showCtaModal?: boolean
  onCtaTryIt?: () => void
  onCtaClose?: () => void
  showCtaInsteadOfButton?: boolean
  animateIcon?: boolean
}

export const AiButton: React.FC<AiButtonProps> = ({
  width = 'auto',
  height = 40,
  isPowered = true,
  style,
  children,
  onClick,
  showCtaModal = false,
  onCtaTryIt,
  onCtaClose,
  showCtaInsteadOfButton = false,
  animateIcon = false,
  ...props
}) => {
  const intlMessages = useDashMessages({
    assistant: 'aiAgent.assistant',
  })

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: width,
    height: height,
    borderRadius: '8px',
    background: colors.GRAYSCALE.X0,
    border: '2px solid #4ECDC4',
    cursor: 'pointer',
    padding: '24px',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    ...style,
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isPowered && onClick) {
      onClick(e)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isPowered && onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      onClick(e)
    }
  }

  // If we should show CTA instead of button, render the CTA modal
  if (showCtaInsteadOfButton) {
    return (
      <AiButtonCtaModal
        isOpen={true}
        onClose={onCtaClose || (() => {})}
        onTryIt={onCtaTryIt || (() => {})}
      />
    )
  }

  return (
    <>
      <div
        style={buttonStyle}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={isPowered ? 0 : -1}
        data-testid="ai-button"
        {...props}
      >
        <AiGradients />
        <AnimatedSparkleIcon
          data-testid="sparkle-icon"
          width={24}
          height={24}
          animate={animateIcon}
          disabled={!isPowered}
        />
        <Text
          typography="bodyEmphasized"
          style={{
            color: !isPowered ? colors.GRAYSCALE.X30 : colors.GRAYSCALE.X80,
            margin: 0,
            padding: 0,
          }}
        >
          {children || intlMessages.assistant}
        </Text>
      </div>
      <AiButtonCtaModal
        isOpen={showCtaModal}
        onClose={onCtaClose || (() => {})}
        onTryIt={onCtaTryIt || (() => {})}
      />
    </>
  )
}

export default AiButton
